import * as React from 'react'

import { Popover as PopoverPrimitive } from '@base-ui/react/popover'
import { inject, observer } from 'mobx-react'

import { LocationType } from '~/client/graph'
import { ListSelector } from '~/client/src/shared/components/ListSelector'
import LocationEquipmentResourcePicker from '~/client/src/shared/components/LocationPicker/LocationEquipmentResourcePicker'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import GlobeView from '~/client/src/shared/models/GlobeView'
import Sitemap from '~/client/src/shared/models/Sitemap'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import { cn } from '~/client/src/shared/utils/cn'

import MapViewItemsSetupStore from '../../stores/MapViewItemsSetup.store'

const restrictedObjectTypes = [
  LocationType.LogisticsObject,
  LocationType.VerticalObject,
]

interface IProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Element the popover positions against — typically the clicked
   * scope chip's DOM node. `null` keeps the popover unmounted.
   */
  anchor: HTMLElement | null
  globe?: GlobeView
  sitemap?: Sitemap
  mapViewItemsSetupStore: MapViewItemsSetupStore
  globeViewsStore?: GlobeViewsStore
  postUpdateAction?: () => void
}

// `-translate-x-full` shifts the arrow back out so its base abuts the popup edge.
// On auto-flip to `data-side=left` we anchor right and rotate 180° so the tip stays toward the anchor.
const PopoverArrow: React.FC = () => (
  <PopoverPrimitive.Arrow
    className={cn(
      'tw:flex tw:items-center tw:justify-center',
      'tw:data-[side=right]:-translate-x-full',
      'tw:data-[side=left]:left-auto tw:data-[side=left]:right-0',
      'tw:data-[side=left]:translate-x-full tw:data-[side=left]:rotate-180',
    )}
  >
    <svg width={10} height={20} viewBox="0 0 10 20" aria-hidden="true">
      <path d="M10 0 L0 10 L10 20 Z" className="tw:fill-popover" />
      <path
        d="M10 0 L0 10 L10 20"
        fill="none"
        strokeWidth={1}
        className="tw:stroke-foreground/10"
      />
    </svg>
  </PopoverPrimitive.Arrow>
)

@inject('globeViewsStore')
@observer
export default class LocationAssociationPopover extends React.Component<IProps> {
  public render(): JSX.Element {
    const { open, onOpenChange, anchor } = this.props
    const { assignedId } = this

    return (
      <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
        {/* Portal so the popover can extend past the parent modal. */}
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Positioner
            anchor={anchor}
            side="right"
            align="start"
            sideOffset={12}
            // Keep arrow clear of the popup's `rounded-lg` corner.
            arrowPadding={14}
            className={cn('tw:isolate tw:z-50')}
          >
            <PopoverPrimitive.Popup
              data-slot="popover-content"
              // Fixed `h-[70vh]` shell so the picker's `h-full` root has a sized ancestor.
              // 400px width preserves legacy modal parity.
              className={cn(
                'tw:relative tw:flex tw:w-[400px] tw:h-[70vh] tw:max-h-(--available-height) tw:origin-(--transform-origin) tw:flex-col tw:overflow-hidden tw:rounded-lg tw:bg-popover tw:text-popover-foreground tw:shadow-md tw:ring-1 tw:ring-foreground/10 tw:outline-hidden tw:duration-100 tw:[&>*]:min-h-0 tw:data-open:animate-in tw:data-open:fade-in-0 tw:data-open:zoom-in-95 tw:data-closed:animate-out tw:data-closed:fade-out-0 tw:data-closed:zoom-out-95',
              )}
            >
              <ListSelector.Title
                title={Localization.translator.selectAssociatedResource}
                onClose={this.handleClose}
              />
              <div className="tw:flex tw:flex-1 tw:flex-col">
                <LocationEquipmentResourcePicker
                  mode="single"
                  stageTreeSelection={true}
                  selectedIds={assignedId ? [assignedId] : []}
                  excludedLocationTypes={restrictedObjectTypes}
                  onSelect={this.handleApply}
                  onClose={this.handleClose}
                />
              </div>
            </PopoverPrimitive.Popup>
            {/* Rendered after the Popup to paint over the Popup's ring outline. */}
            <PopoverArrow />
          </PopoverPrimitive.Positioner>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    )
  }

  private get assignedId(): string | undefined {
    const { globe, sitemap, mapViewItemsSetupStore, globeViewsStore } =
      this.props
    if (globe) return globeViewsStore?.globeAssignedLocations(globe)[0]?.id
    if (sitemap) {
      return mapViewItemsSetupStore.sitemapAssignedItems(sitemap)[0]?.id
    }
  }

  private handleApply = (ids: string[]): void => {
    if ((ids[0] ?? undefined) === this.assignedId) {
      this.handleClose()
      return
    }
    void this.updateAssociations(ids.slice(0, 1))
  }

  private handleClose = (): void => {
    this.props.onOpenChange(false)
  }

  private updateAssociations = async (ids: string[]): Promise<void> => {
    const {
      mapViewItemsSetupStore: {
        updateGlobeAssignedItems,
        updateSitemapAssignedItems,
      },
      globe,
      sitemap,
      postUpdateAction,
    } = this.props
    this.handleClose()
    if (globe) {
      await updateGlobeAssignedItems(ids, globe)
    } else if (sitemap) {
      await updateSitemapAssignedItems(ids, sitemap)
    }
    postUpdateAction?.()
  }
}
